<template>
  <table-wrapper>
    <v-data-table-server
      class="coronet-table--highlightable"
      :items="items"
      v-model="selectedItems"
      :fixed-header="true"
      :page="pagination.page + 1"
      :loading="loading"
      :items-per-page="pagination.pageSize"
      :items-length="total"
      show-select
      @update:options="
        $emit('page-changed', {
          pageSize: $event.itemsPerPage,
          page: $event.page - 1,
        })
      "
      :no-data-text="$t('manageWorkspaces.noData')"
      return-object
    >
      <template #headers="{ allSelected, selectAll, someSelected }">
        <tr>
          <th v-if="allowBulkActions">
            <v-checkbox-btn
              :indeterminate="someSelected && !allSelected"
              :model-value="allSelected"
              color="primary"
              density="compact"
              @update:model-value="selectAll(!allSelected)"
            ></v-checkbox-btn>
          </th>
          <template v-if="selectedItems.length === 0">
            <th>
              {{ $t("manageWorkspaces.table.name") }}
            </th>
            <th>
              {{ $t("manageWorkspaces.table.subscription") }}
            </th>
            <th>
              {{ $t("manageWorkspaces.table.plan") }}
            </th>
            <th v-if="allowBulkActions">
              {{ $t("manageWorkspaces.table.globalPolicy") }}
            </th>
            <th v-else>
              {{ $t("manageWorkspaces.table.createdOn") }}
            </th>
            <th>
              <div>{{ $t("manageWorkspaces.table.protectedUsers") }} /</div>
              <div>{{ $t("manageWorkspaces.table.potentialUsers") }}</div>
            </th>
            <th>
              <span v-html="$t('manageWorkspaces.table.protectedDevices')" />
            </th>
            <th>
              {{ $t("manageWorkspaces.table.usersDevicesLimit") }}
            </th>
            <th>
              {{ $t("manageWorkspaces.table.tickets") }}
            </th>
            <th :aria-label="$t('ariaLabels.actionsColumn')"></th>
          </template>
          <template v-else>
            <th :colspan="9">
              <span>{{ selectedItems.length }} {{ $t("general.selected") }}</span>
              <msp-bulk-actions-menu
                v-if="bulkActionsStore"
                :context="context"
                :selected-items="selectedItems"
                :workspace-store="bulkActionsStore"
                @on-bulk-action-applied="$emit('on-bulk-action-applied')"
              />
            </th>
          </template>
        </tr>
      </template>
      <template #item="{ item, index, internalItem, isSelected, toggleSelect }">
        <tr :key="index" :data-testid="`table-row-${index}`" class="msp-table-row">
          <td class="checkbox-col" v-if="allowBulkActions">
            <v-checkbox-btn
              :model-value="isSelected(internalItem)"
              color="primary"
              density="compact"
              @update:model-value="toggleSelect(internalItem)"
            ></v-checkbox-btn>
          </td>
          <td class="workspace-name-col" :data-testid="`msp-table-name-col-row-${index}`">
            <div class="d-flex align-center py-1">
              <v-icon class="workspace-icon" size="40">{{ iconsMap[item.type] }}</v-icon>
              <div class="ml-3">
                <div class="subtitle2 white-space-normal">
                  {{ item.name }}
                </div>
                <div v-if="item.parentName" class="body2 white-space-normal">
                  {{ $t(`manageWorkspaces.workspaceTypes.${item.type}`) }} /
                  {{ item.parentName }}
                </div>
                <div v-else class="body2">
                  {{ $t(`manageWorkspaces.workspaceTypes.${item.type}`) }}
                </div>
                <div class="caption text-indigo-medium white-space-normal">
                  {{ item.workspaceId }}
                </div>
                <div
                  v-if="item.labels && item.labels.length"
                  class="d-flex align-center w-100 mt-1 flex-wrap ga-1"
                >
                  <v-chip
                    v-for="label in item.labels"
                    :key="label.id"
                    variant="flat"
                    label
                    size="x-small"
                    :color="label.color"
                  >
                    <div class="d-flex caption align-center">
                      {{ label.name }}
                    </div>
                  </v-chip>
                </div>
              </div>
            </div>
          </td>
          <td :data-testid="`msp-table-plan-subscription-row-${index}`">
            <div class="body2 subscription-text" v-if="getSubscription(item).bundlesCount > 0">
              {{ $t(`subscriptions.bundles.${getSubscription(item).bundles[0]}`) }}
              <v-tooltip
                v-if="getSubscription(item).bundlesCount > 1"
                open-delay="300"
                location="top"
              >
                <template #activator="{ props }">
                  +<span class="item-clickable text-decoration-underline" v-bind="props">{{
                    getSubscription(item).bundlesCount - 1
                  }}</span>
                </template>
                <ul>
                  <li v-for="bundle in getSubscription(item).bundles" :key="bundle">
                    {{ $t(`subscriptions.bundles.${bundle}`) }}
                  </li>
                </ul>
              </v-tooltip>
            </div>
            <div
              class="caption text-indigo-medium subscription-text"
              v-if="getSubscription(item).modulesCount > 0"
            >
              {{ $t(`subscriptions.modules.${getSubscription(item).modules[0]}`) }}
              <v-tooltip
                v-if="getSubscription(item).modulesCount > 1"
                open-delay="300"
                location="top"
              >
                <template #activator="{ props }">
                  +<span class="item-clickable text-decoration-underline" v-bind="props">{{
                    getSubscription(item).modulesCount - 1
                  }}</span>
                </template>
                <ul>
                  <li v-for="module in getSubscription(item).modules" :key="module">
                    {{ $t(`subscriptions.modules.${module}`) }}
                  </li>
                </ul>
              </v-tooltip>
            </div>
          </td>
          <td class="subscription-col" :data-testid="`msp-table-plan-col-row-${index}`">
            <div>
              {{ $t(`subscriptions.plans.${item.subscriptionType}`) }}
            </div>
            <div
              v-if="item.trialEndDate"
              class="caption text-indigo-medium"
              :class="{ 'text-orange-base': trialEndsNearExpiration(item) }"
            >
              {{
                $t("manageWorkspaces.table.expiresIn", {
                  n: getTimeUntil(item.trialEndDate),
                })
              }}
            </div>
            <div v-if="item.since" class="caption text-indigo-medium">
              {{ format(item.since, "ll") }}
            </div>
          </td>
          <td
            v-if="allowBulkActions"
            class="global-policy-col"
            :data-testid="`msp-table-daysLeft-col-row-${index}`"
          >
            <div class="body2" v-if="item.mspPolicyName">
              {{ item.mspPolicyName }}
            </div>
            <template v-else> --- </template>
          </td>
          <td v-else class="creation-time-col" :data-testid="`msp-table-daysLeft-col-row-${index}`">
            <div class="body2 creation-text" v-if="item.creationTime">
              {{ format(item.creationTime, "ll") }}
            </div>
            <template v-else> --- </template>
          </td>
          <td class="users-col" :data-testid="`msp-table-protectedUsers-col-row-${index}`">
            <span class="body2">
              <span
                class="body2"
                :class="{ 'coro-link': item.protectedUsers > 0 }"
                @click="viewUsers(item)"
              >
                {{ item.protectedUsers }}
              </span>
              / {{ item.protectableUsers }}
            </span>
          </td>
          <td class="devices-col" :data-testid="`msp-table-protectedDevices-col-row-${index}`">
            <span
              class="body2"
              :class="{ 'coro-link': item.protectedDevices > 0 }"
              @click="viewDevices(item)"
            >
              {{ item.protectedDevices }}
            </span>
          </td>
          <td
            class="users-devices-limit-col"
            :data-testid="`msp-table-usersDevicesLimit-col-row-${index}`"
          >
            <span class="body2">
              {{ getUsersDevicesLimit(item) }}
            </span>
          </td>
          <td class="tickets-col" :data-testid="`msp-table-lastActivity-col-row-${index}`">
            <span class="subtitle2 text-red-dark">{{ item.ticketsCount }}</span>
          </td>
          <td class="checkbox-col coro-table-col--sticky-right" @click="$event.stopPropagation()">
            <msp-workspace-actions-menu
              :item="item"
              :item-actions="itemActions"
              :context="context"
              :workspace-store="workspaceStore"
              :disabled-actions-map="disabledActionsMap"
              action-test-id-prefix="msp-table"
            >
              <template #activator="{ props: actionProps }">
                <v-btn
                  icon="$dots"
                  :data-testid="`users-table-action-btn-row-${index}`"
                  v-bind="actionProps"
                ></v-btn>
              </template>
            </msp-workspace-actions-menu>
          </td>
        </tr>
      </template>
    </v-data-table-server>
  </table-wrapper>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType, ref, watch } from "vue";
import TableWrapper from "@/components/TableWrapper.vue";
import { CoroIcons } from "@/constants/coro-icon";
import {
  MspWorkspaceAction,
  SubscriptionModule,
  SubscriptionType,
  WorkspaceType,
} from "@/constants/workplaces";
import {
  type BaseMspWorkspaceListItem,
  type GlobalMspWorkspaceListItem,
  type MspWorkspaceListItem,
  useMspStore,
} from "@/_store/msp/msp.module.ts";
import type { Pagination } from "@/types";
import { getEnabledModules, isModuleIncludedInBundle } from "@coronet/cui";
import { formatDistanceToNow, getTimeUntil, getFormattedDateTime } from "@/_helpers/date";
import moment from "moment/moment";
import { ManageWorkspacesContext } from "@/constants/msp.ts";
import MspBulkActionsMenu from "@/components/msp/MspBulkActionsMenu.vue";
import MspWorkspaceActionsMenu from "@/components/msp/MspWorkspaceActionsMenu.vue";
import {
  buildGlobalDisabledMap,
  buildSingleHierarchyDisabledMap,
  handleMspWorkspaceViewAction,
} from "@/_helpers/msp-actions.ts";
import { useGlobalWorkspacesStore } from "@/_store/global-workspaces.module.ts";
import { trackGtmEvent } from "@/_helpers/analytics";
import { GtmAction } from "@/constants/gtm.ts";
import startCase from "lodash/startCase";
import { i18n } from "@/plugins/i18n.ts";

export default defineComponent({
  components: {
    TableWrapper,
    MspBulkActionsMenu,
    MspWorkspaceActionsMenu,
  },
  props: {
    items: {
      type: Array as PropType<Array<MspWorkspaceListItem | GlobalMspWorkspaceListItem>>,
      required: true,
    },
    itemActions: {
      type: Array as PropType<Array<MspWorkspaceAction>>,
      required: true,
    },
    total: {
      type: Number,
      required: true,
    },
    pagination: {
      type: Object as PropType<Pagination>,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
    filtersUpdateTime: {
      type: Number,
      required: true,
    },
    allowBulkActions: {
      type: Boolean,
      default: true,
    },
    context: {
      type: String as PropType<ManageWorkspacesContext>,
      default: ManageWorkspacesContext.SINGLE_HIERARCHY,
    },
  },
  emits: ["page-changed", "on-bulk-action-applied"],
  setup(props) {
    const workspaceStore =
      props.context === ManageWorkspacesContext.GLOBAL_WORKSPACES
        ? useGlobalWorkspacesStore()
        : useMspStore();
    const bulkActionsStore =
      props.context === ManageWorkspacesContext.SINGLE_HIERARCHY ? useMspStore() : null;
    const selectedItems = ref<BaseMspWorkspaceListItem[]>([]);

    const viewUsers = async (item: BaseMspWorkspaceListItem) => {
      if (item.protectedUsers > 0) {
        trackGtmEvent(GtmAction.MSP_LIST_VIEW, startCase(MspWorkspaceAction.VIEW_PROTECTED_USERS));
        await handleMspWorkspaceViewAction(MspWorkspaceAction.VIEW_PROTECTED_USERS, item);
      }
    };

    const viewDevices = async (item: BaseMspWorkspaceListItem) => {
      if (item.protectedDevices > 0) {
        trackGtmEvent(GtmAction.MSP_LIST_VIEW, startCase(MspWorkspaceAction.VIEW_DEVICES));
        await handleMspWorkspaceViewAction(MspWorkspaceAction.VIEW_DEVICES, item);
      }
    };

    const getSubscription = (item: BaseMspWorkspaceListItem) => {
      const bundles = item.subscription.bundles ?? [];
      const modules = getEnabledModules(item.subscription).filter((module: SubscriptionModule) => {
        return !isModuleIncludedInBundle(module, item.subscription);
      });
      return {
        bundles,
        modules,
        bundlesCount: bundles.length,
        modulesCount: modules.length,
      };
    };

    const trialEndsNearExpiration = (item: BaseMspWorkspaceListItem) => {
      if (item.subscriptionType !== SubscriptionType.TRIAL) {
        return false;
      }
      const inputDate = moment.utc(item.trialEndDate);

      return inputDate.isBefore(moment.utc().add(7, "days"));
    };

    const getUsersDevicesLimit = (item: BaseMspWorkspaceListItem) => {
      if (!item.limited) {
        return i18n.global.t("manageWorkspaces.filters.options.unlimited");
      }

      const users = item.limits?.numberOfProtectedUsers || 0;
      const devices = item.limits?.numberOfProtectedDevices || 0;
      const overCapacityAllowance = item.limits?.overCapacityAllowance || 0;

      const usersLimit = Math.round(users * (1 + overCapacityAllowance));
      const devicesLimit = Math.round(devices * (1 + overCapacityAllowance));

      return `${usersLimit} / ${devicesLimit}`;
    };

    const disabledActionsMap = computed(() =>
      props.context === ManageWorkspacesContext.GLOBAL_WORKSPACES
        ? buildGlobalDisabledMap()
        : buildSingleHierarchyDisabledMap()
    );

    watch(
      () => props.filtersUpdateTime,
      () => {
        selectedItems.value = [];
      }
    );

    return {
      bulkActionsStore,
      workspaceStore,
      formatDistanceToNow,
      format: getFormattedDateTime,
      coronetIcons: CoroIcons,
      MspWorkspacesAction: MspWorkspaceAction,
      iconsMap: {
        [WorkspaceType.REGULAR]: "icon-regular-plan-s",
        [WorkspaceType.CHANNEL]: "icon-parent-plan-s",
        [WorkspaceType.CHILD]: "icon-child-plan-s",
      },
      selectedItems,
      handleMspWorkspaceViewAction,
      SubscriptionType,
      viewUsers,
      viewDevices,
      getSubscription,
      trialEndsNearExpiration,
      getUsersDevicesLimit,
      getTimeUntil,
      disabledActionsMap,
    };
  },
});
</script>

<style scoped lang="scss">
.checkbox-col {
  width: 30px !important;
}

.workspace-name-col {
  min-width: 300px !important;
}

.users-col {
  min-width: 135px !important;
}

.creation-time-col {
  width: 10% !important;
}

.global-policy-col {
  width: 15% !important;
  word-break: break-word;
  text-wrap: wrap;
}

.subscription-col {
  max-width: 173px !important;
}

.tickets-col {
  min-width: 80px !important;
}

.devices-col {
  min-width: 100px !important;
}

.users-devices-limit-col {
  min-width: 130px !important;
}

.msp-table-row {
  height: 96px;
}

.plan-col {
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 87px;
  overflow: hidden;
}

.white-space-normal {
  white-space: normal !important;
  word-break: break-all;
  min-width: 150px;
}

.subscription-text {
  max-width: 160px;
  min-width: 100px;
  word-break: break-word;
  text-wrap: wrap;
}

.creation-text {
  max-width: 90px;
  min-width: 60px;
  word-break: break-word;
  text-wrap: wrap;
}
</style>
